import { useState, useRef, useEffect } from 'react';
import { Send, Brain, TrendingUp, Users, Building, Target, Activity, X, Minus, Maximize2, Minimize2, BarChart3, Plus, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { chatMarketIntelligence } from '@/services/marketIntelligenceApi';
import { MarketIntelligenceChatsService } from '@/services/marketIntelligenceChats';
import { useToast } from '@/hooks/use-toast';

interface AIResponse {
  id: string;
  query: string;
  response: string;
  data?: any;
  visualizations?: any[];
  timestamp: Date;
  isLoading?: boolean;
  queryType?: string;
  role?: string;
  location?: string;
}

interface ChatSession {
  id: string;
  title: string;
  conversations: AIResponse[];
  createdAt: Date;
  updatedAt: Date;
}

const PROFESSIONAL_QUERIES = [
  "What salary budget should we set for Senior Data Scientists in Berlin?",
  "Which competitors are actively recruiting DevOps Engineers in Munich?",
  "What skills should we prioritize when hiring Product Managers in Germany?",
  "How difficult will it be to hire Machine Learning Engineers in Frankfurt?",
  "What are the market rates for Software Architects across German cities?",
  "Which technical skills are creating hiring bottlenecks for Backend Engineers?",
];

const QUICK_INSIGHTS = [
  {
    icon: TrendingUp,
    title: "Hiring Trends",
    description: "Market demand analysis",
    color: "from-blue-500 to-blue-600"
  },
  {
    icon: Users,
    title: "Talent Pool",
    description: "Candidate availability",
    color: "from-green-500 to-green-600"
  },
  {
    icon: Building,
    title: "Competitors",
    description: "Active employers",
    color: "from-purple-500 to-purple-600"
  },
  {
    icon: Target,
    title: "Skills",
    description: "Critical competencies",
    color: "from-orange-500 to-orange-600"
  }
];

interface MarketIntelligenceOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MarketIntelligenceOverlay({ isOpen, onClose }: MarketIntelligenceOverlayProps) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Get current session
  const currentSession = chatSessions.find(session => session.id === currentSessionId);
  const conversations = currentSession?.conversations || [];

  // Load chat sessions from Supabase when overlay opens
  useEffect(() => {
    const loadChatSessions = async () => {
      if (!isOpen) return;

      try {
        setIsLoadingSessions(true);
        const sessions = await MarketIntelligenceChatsService.getChatSessions();
        
        const parsedSessions = sessions.map((session: any) => ({
          id: session.id,
          title: session.title,
          createdAt: new Date(session.created_at),
          updatedAt: new Date(session.lastUpdate),
          conversations: session.messages.map((msg: any) => ({
            id: msg.id,
            query: msg.query,
            response: msg.response,
            data: msg.data,
            visualizations: msg.visualizations,
            timestamp: new Date(msg.timestamp),
            queryType: msg.query_type,
            role: msg.role,
            location: msg.location,
            isLoading: false
          }))
        }));
        
        setChatSessions(parsedSessions);
        
        // Set the most recent session as current
        if (parsedSessions.length > 0) {
          const mostRecent = parsedSessions.sort((a: ChatSession, b: ChatSession) => 
            b.updatedAt.getTime() - a.updatedAt.getTime()
          )[0];
          setCurrentSessionId(mostRecent.id);
        }
      } catch (error) {
        console.error('Error loading chat sessions:', error);
        toast({
          title: 'Error',
          description: 'Failed to load chat history',
          variant: 'destructive'
        });
      } finally {
        setIsLoadingSessions(false);
      }
    };

    loadChatSessions();
  }, [isOpen, toast]);

  // Auto-scroll to bottom when conversations change
  useEffect(() => {
    if (scrollRef.current) {
      const scrollContainer = scrollRef.current;
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [conversations]);

  // Helper functions
  const createNewSession = async () => {
    try {
      const newSession = await MarketIntelligenceChatsService.createChatSession();
      const sessionData: ChatSession = {
        id: newSession.id,
        title: newSession.title,
        conversations: [],
        createdAt: new Date(newSession.created_at),
        updatedAt: new Date(newSession.lastUpdate)
      };
      
      setChatSessions(prev => [sessionData, ...prev]);
      setCurrentSessionId(newSession.id);

      toast({
        title: 'Success',
        description: 'New chat session created'
      });
    } catch (error) {
      console.error('Error creating session:', error);
      toast({
        title: 'Error',
        description: 'Failed to create new session',
        variant: 'destructive'
      });
    }
  };

  const updateSessionTitle = async (sessionId: string, title: string) => {
    try {
      const success = await MarketIntelligenceChatsService.updateSessionTitle(sessionId, title);
      if (success) {
        setChatSessions(prev => 
          prev.map(session => 
            session.id === sessionId 
              ? { ...session, title, updatedAt: new Date() }
              : session
          )
        );
      }
    } catch (error) {
      console.error('Error updating session title:', error);
      toast({
        title: 'Error',
        description: 'Failed to update session title',
        variant: 'destructive'
      });
    }
  };

  const updateCurrentSession = (updater: (conversations: AIResponse[]) => AIResponse[]) => {
    if (!currentSessionId) return;
    
    setChatSessions(prev => 
      prev.map(session => 
        session.id === currentSessionId 
          ? { 
              ...session, 
              conversations: updater(session.conversations),
              updatedAt: new Date()
            }
          : session
      )
    );
  };

  const handleQuerySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;

    // Create new session if none exists
    if (!currentSessionId) {
      await createNewSession();
      return;
    }

    const newQuery = query.trim();
    setQuery('');
    setIsLoading(true);

    const queryId = `query-${Date.now()}`;
    const newConversation: AIResponse = {
      id: queryId,
      query: newQuery,
      response: '',
      timestamp: new Date(),
      isLoading: true
    };

    // Auto-generate session title from first query
    if (conversations.length === 0) {
      const title = newQuery.length > 50 ? newQuery.substring(0, 50) + '...' : newQuery;
      await updateSessionTitle(currentSessionId, title);
    }

    updateCurrentSession(prev => [...prev, newConversation]);

    try {
      const apiRes = await chatMarketIntelligence(newQuery);
      const conversationData = {
        query: newQuery,
        response: apiRes.response,
      };

      const savedConversation = await MarketIntelligenceChatsService.addConversation(
        currentSessionId,
        conversationData
      );

      updateCurrentSession(prev => 
        prev.map(conv => 
          conv.id === queryId 
            ? { 
                id: savedConversation.id,
                query: savedConversation.query,
                response: savedConversation.response,
                timestamp: new Date(savedConversation.timestamp),
                isLoading: false 
              }
            : conv
        )
      );
    } catch (error) {
      console.error('Error analyzing query:', error);
      
      // Save error response to Supabase
      const errorResponse = "I apologize for the technical difficulty. Our market intelligence system is temporarily unavailable. Please try your query again in a moment, or contact support if the issue persists.";
      
      try {
        const savedConversation = await MarketIntelligenceChatsService.addConversation(
          currentSessionId,
          {
            query: newQuery,
            response: errorResponse
          }
        );

        updateCurrentSession(prev => 
          prev.map(conv => 
            conv.id === queryId 
              ? { 
                  id: savedConversation.id,
                  query: savedConversation.query,
                  response: savedConversation.response,
                  timestamp: new Date(savedConversation.timestamp),
                  isLoading: false 
                }
              : conv
          )
        );
      } catch (saveError) {
        console.error('Error saving conversation:', saveError);
        updateCurrentSession(prev => 
          prev.map(conv => 
            conv.id === queryId 
              ? { 
                  ...conv, 
                  response: errorResponse,
                  isLoading: false 
                }
              : conv
          )
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuery = (suggestedQuery: string) => {
    setQuery(suggestedQuery);
  };

  const formatResponse = (response: string) => {
    // Clean up formatting and structure the response
    let cleanResponse = response
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')
      .replace(/•\s*([^\n]+)/g, '<li class="ml-4 text-gray-700">$1</li>')
      .replace(/\n\n+/g, '</p><p class="mt-4 text-gray-700 leading-relaxed">')
      .replace(/\n/g, '<br/>');

    // Structure the response with proper paragraphs
    cleanResponse = '<p class="text-gray-700 leading-relaxed">' + cleanResponse + '</p>';
    
    // Convert bullet lists to proper HTML lists
    cleanResponse = cleanResponse.replace(/(<li[^>]*>.*?<\/li>)/gs, '<ul class="list-disc space-y-2 mt-3 mb-4 ml-4">$1</ul>');
    
    // Clean up any remaining formatting issues
    cleanResponse = cleanResponse.replace(/<\/p><p[^>]*><ul/g, '</p><ul');
    cleanResponse = cleanResponse.replace(/<\/ul><\/p>/g, '</ul>');
    
    return cleanResponse;
  };

  const renderVisualization = (visualization: any) => {
    switch (visualization.type) {
      case 'salary_chart':
        return (
          <Card className="mt-4 border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                </div>
                Salary Benchmarks
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {visualization.data.map((item: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100 shadow-sm">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{item.role}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{item.experience}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">€{item.salary.toLocaleString()}</p>
                      <Badge variant="secondary" className="mt-1 bg-green-100 text-green-700 border-green-200 text-xs">
                        {item.trend}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      
      case 'demand_metrics':
        return (
          <Card className="mt-4 border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <div className="p-1.5 bg-green-100 rounded-lg">
                  <Activity className="h-4 w-4 text-green-600" />
                </div>
                Market Dynamics
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-3">
                {visualization.data.map((metric: any, index: number) => (
                  <div key={index} className="bg-white p-3 rounded-lg border border-green-100 shadow-sm">
                    <p className="text-xs font-medium text-gray-600 mb-1">{metric.label}</p>
                    <p className="text-lg font-bold text-green-600 mb-1">{metric.value}</p>
                    <p className="text-xs text-gray-500">{metric.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      
      default:
        return null;
    }
  };

  // Switch to a different session
  const switchToSession = (sessionId: string) => {
    setCurrentSessionId(sessionId);
    setShowSidebar(false); // Close sidebar on mobile after selection
  };

  if (!isOpen) return null;

  // Minimized state
  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsMinimized(false)}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full w-14 h-14 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110"
        >
          <Brain className="w-6 h-6" />
        </Button>
      </div>
    );
  }

  const overlaySize = isMaximized 
    ? "top-4 left-4 right-4 bottom-4" 
    : "bottom-6 right-6 w-[580px] h-[680px]";

  return (
    <div className={`fixed ${overlaySize} z-50`}>
      <div className="h-full bg-white rounded-2xl shadow-2xl border border-gray-200/60 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowSidebar(!showSidebar)}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 p-2"
                title="Toggle Chat History"
              >
                <MessageSquare className="h-3 w-3" />
              </Button>
              <Button
                onClick={createNewSession}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 px-3 py-1 text-xs"
                title="Start New Chat"
              >
                <Plus className="h-3 w-3 mr-1" />
                New Chat
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMaximized(!isMaximized)}
                className="text-white hover:bg-white/20 p-2"
              >
                {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(true)}
                className="text-white hover:bg-white/20 p-2"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:bg-white/20 p-2"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Quick Insights */}
          <div className="grid grid-cols-2 gap-2 mt-2">
            {QUICK_INSIGHTS.map((insight, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-2 border border-white/20">
                <insight.icon className="h-3 w-3 text-white mb-1" />
                <h3 className="font-medium text-white text-xs mb-0.5">{insight.title}</h3>
                <p className="text-blue-100 text-xs leading-tight">{insight.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex min-h-0">
          {/* Sidebar - Chat History */}
          {showSidebar && (
            <div className="w-56 bg-slate-800 flex-shrink-0 flex flex-col border-r border-slate-700">
              {/* Sidebar Header */}
              <div className="p-3 border-b border-slate-700">
                <h3 className="text-white text-sm font-medium">Previous Chats</h3>
              </div>
              
              {/* Chat Sessions List */}
              <div className="flex-1 overflow-y-auto p-2">
                {isLoadingSessions ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                ) : chatSessions.length === 0 ? (
                  <div className="text-slate-400 text-xs text-center py-8">
                    <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-50" />
                    <p>No chats yet</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {chatSessions.map((session) => (
                      <div
                        key={session.id}
                        className={`p-2 rounded-lg cursor-pointer transition-colors ${
                          session.id === currentSessionId 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                        onClick={() => switchToSession(session.id)}
                      >
                        <p className="text-xs font-medium truncate">{session.title}</p>
                        <p className="text-xs opacity-60 mt-1">
                          {session.conversations.length} messages
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chat Content Area */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Conversation Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={scrollRef}>
            {isLoadingSessions ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Start Your Market Analysis</h3>
                  <p className="text-gray-600 text-sm">Ask questions about hiring, salaries, or market trends</p>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 text-sm">Try these questions:</h4>
                  {PROFESSIONAL_QUERIES.slice(0, 3).map((query, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestedQuery(query)}
                      className="block w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
                    >
                      {query}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              conversations.map((conversation, index) => (
                <div key={index} className="space-y-4">
                  {/* User Query */}
                  <div className="flex justify-end">
                    <div className="bg-blue-600 text-white rounded-2xl px-4 py-3 max-w-[75%]">
                      <p className="text-sm">{conversation.query}</p>
                    </div>
                  </div>
                  
                  {/* AI Response */}
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-2xl px-4 py-3 max-w-[85%]">
                      {conversation.isLoading ? (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span className="text-sm text-gray-600">Analyzing...</span>
                        </div>
                      ) : (
                        <div>
                          <div
                            className="text-sm leading-relaxed"
                            dangerouslySetInnerHTML={{
                              __html: formatResponse(conversation.response)
                            }}
                          />
                          {conversation.visualizations && conversation.visualizations.length > 0 && (
                            <div className="mt-3">
                              {conversation.visualizations.map((viz, vizIndex) => (
                                <div key={vizIndex} className="text-xs">
                                  {renderVisualization(viz)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

            {/* Input Area */}
            <div className="flex-shrink-0 border-t border-gray-200 p-2">
              <form onSubmit={handleQuerySubmit} className="flex gap-2">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask about market trends, salaries, or hiring insights..."
                  className="flex-1 text-sm h-9"
                  disabled={isLoading}
                />
                <Button type="submit" disabled={isLoading || !query.trim()} size="sm" className="h-9 px-3">
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 